import { ModNavigationItem } from '@moderneinc/design-system-components';
import { borderRadius, semanticColors } from '@moderneinc/design-system-tokens';
import kebabcase from 'lodash.kebabcase';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';
import { navBetaTagSx } from '../../../constants/sx';
import { alphaVar } from '../../../helpers/alpha.helper';
import { determineHref } from '../../../helpers/link.helper';
import { useCommandPalette } from '../../../hooks/use-command-palette.hooks';
import type { NavItem } from '../left-navigation/left-navigation.component';

// Hover/selected icon tile: blue tint, a stronger border, and a tighter radius
// than the design system's default (xS 4px vs s 8px) — per the mock. The tile
// is invisible (transparent border, no fill) until active, so only these
// states need it.
const ICON_TILE_ACTIVE_SX = {
  backgroundColor: alphaVar(semanticColors.control.bgChecked, 0.1),
  borderColor: semanticColors.color.border.strong,
  borderRadius: `${borderRadius.xS}px`
};

type LeftNavigationMenuItemProps = NavItem & {
  onClick?: () => void;
  registerCommand?: boolean;
};
export const LeftNavigationMenuItem: FunctionComponent<
  LeftNavigationMenuItemProps
> = ({
  href,
  shortcut,
  label,
  description,
  icon,
  onClick,
  beta,
  tag,
  registerCommand = false
}) => {
  const { asPath, push } = useRouter();
  const selectedMenuItemRegex = new RegExp(`^${href}`, 'i');

  const shouldRegisterCommand = registerCommand && shortcut;

  useCommandPalette(
    shouldRegisterCommand
      ? [
          {
            label: `Go to ${label}`,
            description: description,
            action: (e) => {
              if (e && typeof e.preventDefault === 'function') {
                e.preventDefault();
              }
              return push(determineHref(href));
            },
            keyboardShortcuts: shortcut
              ? {
                  key: shortcut,
                  modifier: {
                    shift: true
                  }
                }
              : undefined
          }
        ]
      : [],
    [href, shortcut, label, description, registerCommand]
  );

  const isSelected = selectedMenuItemRegex.test(asPath);

  return (
    <ModNavigationItem
      component={Link}
      prefetch={false}
      data-testid={`global-nav-${kebabcase(label)}-button`}
      selected={isSelected}
      aria-current={isSelected ? 'page' : undefined}
      // TODO(design-system): ModNavigationItem paints the hover/selected tile
      // with button.secondary.* (warm grey, subtle border, 8px radius); the
      // mock uses a blue tint, a stronger border, and a tighter radius. There's
      // no slotProp for the icon container, so this reaches into
      // .mod-nav-icon-padding — fragile, move upstream when the design system
      // rebinds it.
      sx={{
        '&:hover .mod-nav-icon-padding': ICON_TILE_ACTIVE_SX,
        ...(isSelected && {
          '& .mod-nav-icon-padding': ICON_TILE_ACTIVE_SX
        })
      }}
      href={determineHref(href)}
      onClick={onClick}
      icon={icon}
      label={label}
      tooltip={description}
      tag={beta ? (tag ?? 'Beta') : tag}
      slotProps={beta ? { badge: { sx: navBetaTagSx } } : undefined}
    />
  );
};
